import {
  CASHFLOW_SCENARIO_LABELS,
  CASHFLOW_SCENARIOS,
  type CashflowProjectedItem,
  type CashflowProjection,
  type CashflowScenarioCode,
  type CashflowView,
  cashflowScenarioSchema,
  cashflowViewSchema,
  MAX_CASHFLOW_HORIZON_DAYS,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Drawer,
  FormField,
  Select,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Settings2, TrendingUp } from "lucide-react";
import { type ReactElement, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { Amount, useCompanyStations } from "../../../features/accounting/shared.js";
import {
  useCashflowCompare,
  useCashflowItems,
  useCashflowPermissions,
  useCashflowProjection,
} from "../../../features/cashflow/queries.js";
import { CashflowSettingsDialog } from "../../../features/cashflow/settings-dialog.js";
import {
  CashflowSourceLink,
  itemKindLabel,
  ShortfallBadge,
  useStationScope,
} from "../../../features/cashflow/shared.js";
import { HORIZON_OPTIONS, isInflow } from "../../../features/cashflow/scenario-values.js";
import { ExpensesPage, Field, Section } from "../../../features/expenses/shared.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";

const searchSchema = z.object({
  ...companySearchShape,
  scenario: cashflowScenarioSchema.optional().catch(undefined),
  view: cashflowViewSchema.optional().catch(undefined),
  horizon: z.coerce.number().int().min(1).max(MAX_CASHFLOW_HORIZON_DAYS).optional().catch(undefined),
  stationId: z.string().uuid().optional().catch(undefined),
  day: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
});
type CashflowSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/nakit-akis/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: CashflowPage,
});

function CashflowPage(): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const permissions = useCashflowPermissions();
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <ExpensesPage
      title={t("menu.cashFlow")}
      description={t("cashFlow.description")}
      breadcrumb={[{ label: t("menu.cashFlow") }]}
      actions={
        <Button variant="outline" onClick={() => setSettingsOpen(true)}>
          <Settings2 aria-hidden="true" />
          {t("cashFlow.settings.title")}
        </Button>
      }
    >
      {({ companyId }) => (
        <>
          {permissions.canRead ? (
            <Projection companyId={companyId} search={search} />
          ) : (
            <Alert tone="info">{t("cashFlow.noPermission")}</Alert>
          )}
          <CashflowSettingsDialog
            companyId={companyId}
            open={settingsOpen}
            readOnly={!permissions.canManage}
            onOpenChange={setSettingsOpen}
          />
        </>
      )}
    </ExpensesPage>
  );
}

function Projection({ companyId, search }: { companyId: string; search: CashflowSearch }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const stations = useCompanyStations(companyId);
  const stationScope = useStationScope();

  const scenario = search.scenario ?? "BASE";
  const view = search.view ?? "DAILY";
  const horizonDays = search.horizon ?? 30;
  // İstasyon kapsamlı kullanıcı ŞİRKET GENELİNİ göremez: kendi istasyonu zorunlu seçilidir.
  const stationId = stationScope.restricted
    ? (search.stationId ?? stationScope.allowedStationIds[0])
    : search.stationId;

  const filters = { scenario, view, horizonDays, stationId };
  const projection = useCashflowProjection(companyId, filters);
  const setSearch = (patch: Partial<CashflowSearch>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value)),
      replace: true,
    });

  const visibleStations = (stations.data ?? []).filter(
    (station) => !stationScope.restricted || stationScope.allowedStationIds.includes(station.id),
  );

  return (
    <Tabs
      className="grid gap-4"
      value={view}
      onValueChange={(next) => setSearch({ view: next as CashflowView, day: undefined })}
    >
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <FormField label={t("cashFlow.filters.scenario")} className="w-44">
          {(control) => (
            <Select
              {...control}
              value={scenario}
              onChange={(event) =>
                setSearch({ scenario: event.target.value as CashflowScenarioCode, day: undefined })
              }
            >
              {CASHFLOW_SCENARIOS.map((code) => (
                <option key={code} value={code}>
                  {CASHFLOW_SCENARIO_LABELS[code]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("cashFlow.filters.horizon")} className="w-36">
          {(control) => (
            <Select
              {...control}
              value={String(horizonDays)}
              onChange={(event) => setSearch({ horizon: Number(event.target.value), day: undefined })}
            >
              {HORIZON_OPTIONS.map((days) => (
                <option key={days} value={days}>
                  {t("cashFlow.filters.horizonDays", { count: days })}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("cashFlow.filters.station")} className="w-56">
          {(control) => (
            <Select
              {...control}
              value={stationId ?? ""}
              onChange={(event) => setSearch({ stationId: event.target.value || undefined, day: undefined })}
            >
              {/* Kapsamı kısıtlı kullanıcıda "şirket geneli" seçeneği hiç çizilmez. */}
              {stationScope.restricted ? null : (
                <option value="">{t("cashFlow.filters.wholeCompany")}</option>
              )}
              {visibleStations.map((station) => (
                <option key={station.id} value={station.id}>
                  {station.code} {station.name}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <TabsList aria-label={t("cashFlow.filters.view")}>
          <TabsTrigger value="DAILY">{t("cashFlow.filters.daily")}</TabsTrigger>
          <TabsTrigger value="WEEKLY">{t("cashFlow.filters.weekly")}</TabsTrigger>
        </TabsList>
      </Card>

      {/* Etkin görünümün paneli: tetikleyicinin `aria-controls` hedefi var olmalı (axe: aria-valid-attr-value). */}
      <TabsContent value={view} className="grid gap-4 pt-0">
        {projection.error ? (
          <Alert tone="danger">{errorMessage(projection.error)}</Alert>
        ) : !projection.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <>
            <Summary projection={projection.data} />
            {view === "DAILY" ? (
              <DailyTable projection={projection.data} onSelectDay={(date) => setSearch({ day: date })} />
            ) : (
              <WeeklyTable projection={projection.data} />
            )}
            <Compare companyId={companyId} horizonDays={horizonDays} stationId={stationId} />
          </>
        )}
      </TabsContent>

      <Drawer
        open={search.day !== undefined}
        onOpenChange={(open) => {
          if (!open) setSearch({ day: undefined });
        }}
        title={search.day ? t("cashFlow.dayTitle", { date: formatDate(search.day) }) : ""}
      >
        {search.day ? (
          <DayItems
            companyId={companyId}
            date={search.day}
            scenario={scenario}
            horizonDays={horizonDays}
            stationId={stationId}
          />
        ) : null}
      </Drawer>
    </Tabs>
  );
}

function Summary({ projection }: { projection: CashflowProjection }): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  const shortfallCount = projection.shortfallDays.length;
  return (
    <Card className="grid gap-3 p-4" aria-labelledby={headingId}>
      <h2 id={headingId} className="text-base font-semibold">
        {t("cashFlow.summary")}
      </h2>
      <dl className="grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <Field label={t("cashFlow.fields.openingBalance")}>
          <Amount value={projection.openingBalance} colorize />
        </Field>
        <Field label={t("cashFlow.fields.overdraftLimit")}>
          <Amount value={projection.overdraftLimit} />
        </Field>
        <Field label={t("cashFlow.fields.closingBalance")}>
          <Amount value={projection.totals.closingBalance} colorize />
        </Field>
        <Field label={t("cashFlow.fields.lowestAvailable")}>
          <span className="flex flex-col">
            <Amount value={projection.lowestAvailable.amount} colorize />
            <span className="text-xs text-muted-foreground">
              {formatDate(projection.lowestAvailable.date)}
            </span>
          </span>
        </Field>
        <Field label={t("cashFlow.fields.shortfallDays")}>
          {shortfallCount === 0 ? (
            <StatusBadge tone="success">{t("cashFlow.noShortfallForecast")}</StatusBadge>
          ) : (
            <StatusBadge tone="danger">
              {t("cashFlow.shortfallDayCount", { count: shortfallCount })}
            </StatusBadge>
          )}
        </Field>
      </dl>

      {projection.unknownAmountObligations > 0 ? (
        <Alert tone="warning" title={t("cashFlow.unknownAmountTitle")}>
          {t("cashFlow.unknownAmountBody", { count: projection.unknownAmountObligations })}{" "}
          <Link to="/vergi-takvimi" search={{ view: "list" }} className="underline underline-offset-4">
            {t("menu.taxCalendar")}
          </Link>
        </Alert>
      ) : null}
      {projection.beyondHorizon > 0 ? (
        <p className="text-xs text-muted-foreground">
          {t("cashFlow.beyondHorizon", { count: projection.beyondHorizon })}
        </p>
      ) : null}
      {/* İstasyon görünümünde banka ve KMH taşınmaz; sunucu bunu `notes` ile bildirir. */}
      {projection.notes.map((note) => (
        <Alert key={note} tone="info">
          {note}
        </Alert>
      ))}
    </Card>
  );
}

function DailyTable({
  projection,
  onSelectDay,
}: {
  projection: CashflowProjection;
  onSelectDay: (date: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Section title={t("cashFlow.dailyTable")}>
      {/* Grafik yerine tabloyla aynı veriyi taşıyan erişilebilir özet (grafik kitaplığı projede yok). */}
      <p className="text-xs text-muted-foreground">{t("cashFlow.dailyTableHint")}</p>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <caption className="sr-only">{t("cashFlow.dailyTable")}</caption>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 text-left">
                {t("cashFlow.fields.date")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("cashFlow.fields.inflow")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("cashFlow.fields.outflow")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("cashFlow.fields.net")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("cashFlow.fields.closing")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("cashFlow.fields.available")}
              </th>
              <th scope="col" className="px-3 py-2 text-left">
                {t("common.status")}
              </th>
            </tr>
          </thead>
          <tbody>
            {projection.days.map((day) => (
              <tr key={day.date} className={day.shortfall ? "border-t bg-destructive/10" : "border-t"}>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    className="underline underline-offset-4"
                    onClick={() => onSelectDay(day.date)}
                  >
                    {formatDate(day.date)}
                  </button>
                </td>
                <td className="px-3 py-2 text-right">
                  <Amount value={day.inflow} blankZero />
                </td>
                <td className="px-3 py-2 text-right">
                  <Amount value={day.outflow} blankZero />
                </td>
                <td className="px-3 py-2 text-right">
                  <Amount value={day.net} colorize blankZero />
                </td>
                <td className="px-3 py-2 text-right">
                  <Amount value={day.closingBalance} colorize />
                </td>
                <td className="px-3 py-2 text-right font-semibold">
                  <Amount value={day.available} colorize />
                </td>
                <td className="px-3 py-2">
                  <ShortfallBadge shortfall={day.shortfall} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function WeeklyTable({ projection }: { projection: CashflowProjection }): ReactElement {
  const { t } = useTranslation();
  return (
    <Section title={t("cashFlow.weeklyTable")}>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <caption className="sr-only">{t("cashFlow.weeklyTable")}</caption>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 text-left">
                {t("cashFlow.fields.week")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("cashFlow.fields.inflow")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("cashFlow.fields.outflow")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("cashFlow.fields.net")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("cashFlow.fields.closing")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("cashFlow.fields.minAvailable")}
              </th>
              <th scope="col" className="px-3 py-2 text-left">
                {t("common.status")}
              </th>
            </tr>
          </thead>
          <tbody>
            {projection.weeks.map((week) => (
              <tr key={week.weekStart} className={week.shortfall ? "border-t bg-destructive/10" : "border-t"}>
                <td className="px-3 py-2">
                  {formatDate(week.from)} – {formatDate(week.to)}
                </td>
                <td className="px-3 py-2 text-right">
                  <Amount value={week.inflow} blankZero />
                </td>
                <td className="px-3 py-2 text-right">
                  <Amount value={week.outflow} blankZero />
                </td>
                <td className="px-3 py-2 text-right">
                  <Amount value={week.net} colorize blankZero />
                </td>
                <td className="px-3 py-2 text-right">
                  <Amount value={week.closingBalance} colorize />
                </td>
                <td className="px-3 py-2 text-right font-semibold">
                  <Amount value={week.minAvailable} colorize />
                </td>
                <td className="px-3 py-2">
                  <ShortfallBadge shortfall={week.shortfall} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

/** Üç senaryonun yan yana özeti: kapanış, en düşük nakit + günü, açık günü sayısı. */
function Compare({
  companyId,
  horizonDays,
  stationId,
}: {
  companyId: string;
  horizonDays: number;
  stationId: string | undefined;
}): ReactElement | null {
  const { t } = useTranslation();
  const compare = useCashflowCompare(companyId, horizonDays, stationId);
  if (compare.error) return <Alert tone="danger">{errorMessage(compare.error)}</Alert>;
  if (!compare.data) return null;
  return (
    <Section title={t("cashFlow.compare.title")}>
      <p className="text-xs text-muted-foreground">{t("cashFlow.compare.hint")}</p>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <caption className="sr-only">{t("cashFlow.compare.title")}</caption>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 text-left">
                {t("cashFlow.filters.scenario")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("cashFlow.fields.closing")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("cashFlow.fields.lowestAvailable")}
              </th>
              <th scope="col" className="px-3 py-2 text-left">
                {t("cashFlow.fields.lowestDate")}
              </th>
              <th scope="col" className="px-3 py-2 text-left">
                {t("cashFlow.fields.shortfallDays")}
              </th>
            </tr>
          </thead>
          <tbody>
            {compare.data.scenarios.map((row) => (
              <tr key={row.scenario} className="border-t">
                <td className="px-3 py-2 font-medium">{CASHFLOW_SCENARIO_LABELS[row.scenario]}</td>
                <td className="px-3 py-2 text-right">
                  <Amount value={row.closingBalance} colorize />
                </td>
                <td className="px-3 py-2 text-right">
                  <Amount value={row.lowestAvailable} colorize />
                </td>
                <td className="px-3 py-2">{formatDate(row.lowestDate)}</td>
                <td className="px-3 py-2">
                  {row.shortfallDayCount === 0 ? (
                    <StatusBadge tone="success">{t("cashFlow.noShortfallForecast")}</StatusBadge>
                  ) : (
                    <StatusBadge tone="danger">
                      {t("cashFlow.shortfallDayCount", { count: row.shortfallDayCount })}
                      {row.firstShortfallDate ? ` · ${formatDate(row.firstShortfallDate)}` : ""}
                    </StatusBadge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function DayItems({
  companyId,
  date,
  scenario,
  horizonDays,
  stationId,
}: {
  companyId: string;
  date: string;
  scenario: CashflowScenarioCode;
  horizonDays: number;
  stationId: string | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const items = useCashflowItems(companyId, date, { scenario, horizonDays, stationId });
  if (items.error) return <Alert tone="danger">{errorMessage(items.error)}</Alert>;
  if (!items.data) return <SkeletonRows rows={4} />;
  return (
    <div className="grid gap-3">
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <Field label={t("cashFlow.fields.inflow")}>
          <Amount value={items.data.inflow} />
        </Field>
        <Field label={t("cashFlow.fields.outflow")}>
          <Amount value={items.data.outflow} />
        </Field>
      </dl>
      <ul className="grid gap-2">
        {items.data.items.map((item: CashflowProjectedItem) => (
          <li key={`${item.sourceType}:${item.sourceId}`} className="grid gap-1 rounded-md border p-3">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge tone="neutral">{itemKindLabel(item.kind)}</StatusBadge>
              {item.overdue ? <StatusBadge tone="danger">{t("cashFlow.overdue")}</StatusBadge> : null}
              {item.inPaymentBatch ? (
                <StatusBadge tone="info">{t("cashFlow.inPaymentBatch")}</StatusBadge>
              ) : null}
              <span className="ms-auto font-semibold">
                {isInflow(item.signedAmount) ? "+" : "−"}
                <Amount value={item.projectedAmount} />
              </span>
            </div>
            <span className="text-sm">
              <CashflowSourceLink item={item} />
            </span>
            {item.date !== item.projectedDate ? (
              <span className="text-xs text-muted-foreground">
                {t("cashFlow.originalDueDate", { date: formatDate(item.date) })}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {items.data.items.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <TrendingUp className="size-4" aria-hidden="true" />
          {t("cashFlow.noItems")}
        </p>
      ) : null}
    </div>
  );
}
